import { UISref, useRouter } from "@uirouter/react";
import moment from "moment";
import React, { FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "../../../components/button";
import { Icon } from "../../../components/icons";
import { Loader } from "../../../components/loader";
import { Table } from "../../../components/table";
import { TableHeaders } from "../../../components/table/Head";
import { Widget } from "../../../components/widget";
import { Pageable } from "../../../services/pageable";
import { AppStore } from "../../../store";
import { useFetchable } from "../../../store/common/fetchable";
import { LaunchConfigurationsActions } from "../../../store/launch-configurations/actions";
import { LaunchConfigurationsDTO } from "../../../store/launch-configurations/models/dto";
import { LaunchConfigurations } from "../../../store/launch-configurations/reducer";
import { colours } from "../../../styles/colours";
import { LaunchConfigurationsRoutes as routes } from "../routes";
import { NoLaunchConfigurations } from "./base";

type SingleLaunchConfiguration = LaunchConfigurationsDTO.Responses.GetSingle.T;
type fieldNames = keyof SingleLaunchConfiguration;

const headers: TableHeaders<fieldNames> = [
  {
    name: "configuration",
    title: "Launch Name",
  },
  {
    name: "state",
    title: "State",
  },
  {
    name: "pages",
    title: "URL",
  },
  {
    name: "id",
    title: "",
    centered: true,
    hiddenMobile: true,
  },
];

type LaunchConfigurationListProps = { hideEmail?: boolean };

export const LaunchConfigurationList: FunctionComponent<LaunchConfigurationListProps> = (
  props
) => {
  const data = useFetchable(
    LaunchConfigurations.launchConfigurations,
    useMemo<Pageable.T<keyof LaunchConfigurationsDTO.Responses.GetSingle.T>>(
      () => ({
        pagination: {
          size: 50,
          page: 0,
        },
      }),
      []
    )
  );

  return (
    <Loader data={[data]}>
      {([launchConfigurationList]) => (
        <Widget title="My LaunchConfigurations">
          <PresentationalLaunchConfigurationList
            launchConfigurationList={launchConfigurationList}
            {...props}
          />
        </Widget>
      )}
    </Loader>
  );
};

export const PresentationalLaunchConfigurationList: FunctionComponent<
  {
    launchConfigurationList: LaunchConfigurationsDTO.Responses.GetAll.T;
  } & LaunchConfigurationListProps
> = ({ launchConfigurationList, hideEmail }) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const pageableState = useSelector<
    AppStore,
    AppStore["launchConfigurations"]["launchConfigurations"]["pagination"]
  >((store) => store.launchConfigurations.launchConfigurations.pagination);

  const onPaginationChange = (x: Pageable.T<fieldNames>) =>
    dispatch(LaunchConfigurationsActions.setPagination(x));

  return (
    <>
      {launchConfigurationList.pageable.totalElements < 1 ? (
        <NoLaunchConfigurations />
      ) : (
        <>
          <Table
            data={launchConfigurationList.content}
            head={headers}
            renderFields={{
              id: ({ id }) => (
                <UISref to={routes.names.single} params={{ id }}>
                  <a>
                    <PrimaryButton variant="contained">
                      <Icon
                        type="export"
                        rotateAmount={270}
                        colour1={colours.white}
                      />
                    </PrimaryButton>
                  </a>
                </UISref>
              ),
              configuration: ({ configuration }) => {
                if (configuration.supportedFormLanguages.includes("en_GB")) {
                  return <>{configuration.launchName["en_GB"]}</>;
                } else {
                  return (
                    <>
                      {configuration.launchName[
                        configuration.supportedFormLanguages[0]
                      ] ?? null}
                    </>
                  );
                }
              },
              pages: ({ id }) => {
                return (
                  <a target="_blank">{`${window.location.origin}/launches/${id}`}</a>
                );
              },
            }}
            pagination={launchConfigurationList.pageable}
            onPaginationChange={onPaginationChange}
            pageableState={pageableState}
          />
        </>
      )}
    </>
  );
};
